/**
 * Professional Analytics Dashboard
 * 
 * Enterprise-grade monitoring interface with data tables, visualizations,
 * and structured analytics. Designed for professional use with clean,
 * McKinsey-style presentation.
 */

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import './Logger.css';
import { getApiEndpoint } from 'utils/centralizedConfig';
import { getApiClient } from 'utils/apiClient';
import { buildSdkUrl } from 'utils/sdkUrlHelper';
import AuthContext from 'contexts/AuthContext';

const Logger = ({ sidebarOpen }) => {
  // Load persisted tab state from localStorage
  const getInitialMainTab = () => {
    const stored = localStorage.getItem('bsp_tech_main_tab');
    // Validate stored value is a valid tab
    const validTabs = ['search', 'besper-bot', 'whats-besper', 'browser'];
    if (stored && validTabs.includes(stored)) {
      return stored;
    }
    return 'search'; // Default to Search
  };

  const getInitialBrowserSubTab = () => {
    const stored = localStorage.getItem('bsp_tech_browser_subtab');
    return stored || 'overview';
  };

  const getInitialBotSubTab = () => {
    const stored = localStorage.getItem('bsp_tech_bot_subtab');
    // If stored value is 'implementation', default to 'assistant' (implementation tab removed)
    return (stored && stored !== 'implementation') ? stored : 'assistant'; // Default to Assistant
  };

  const [isOpen, setIsOpen] = useState(false);
  const [mainTab, setMainTab] = useState(getInitialMainTab);
  const [browserSubTab, setBrowserSubTab] = useState(getInitialBrowserSubTab);
  const [botSubTab, setBotSubTab] = useState(getInitialBotSubTab);
  const sessionPreCreatedRef = useRef(false);
  const [logs, setLogs] = useState([]);
  const [networkRequests, setNetworkRequests] = useState([]);


  const [logFilter, setLogFilter] = useState('all');
  const [logSearch, setLogSearch] = useState('');
  const [networkSearch, setNetworkSearch] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [timeRange, setTimeRange] = useState('1h');
  const originalConsoleRef = useRef({});
  const logCounterRef = useRef(0);
  const hideWidgetTriggerIntervalRef = useRef(null);

  // Persist tab selections to localStorage
  useEffect(() => {
    localStorage.setItem('bsp_tech_main_tab', mainTab);
  }, [mainTab]);

  useEffect(() => {
    localStorage.setItem('bsp_tech_browser_subtab', browserSubTab);
  }, [browserSubTab]);

  useEffect(() => {
    // Don't save 'implementation' to localStorage since that tab is removed
    if (botSubTab !== 'implementation') {
      localStorage.setItem('bsp_tech_bot_subtab', botSubTab);
    }
  }, [botSubTab]);
  
  // If implementation tab is somehow selected, redirect to assistant
  useEffect(() => {
    if (botSubTab === 'implementation') {
      setBotSubTab('assistant');
    }
  }, [botSubTab]);

  // Hide external widget trigger when technical interface is expanded
  // Use JavaScript to override inline styles since they have !important
  useEffect(() => {
    const hideExternalWidgetTrigger = () => {
      // Find the external public_bot_external widget (not the one in technical interface)
      const allWidgets = document.querySelectorAll('besper-bot-widget');
      allWidgets.forEach(widget => {
        const botId = widget.getAttribute('bot-id');
        // Only hide the external public_bot_external widget
        if (botId === 'public_bot_external') {
          // Check if it's NOT inside the technical interface container
          const isInsideTechnicalInterface = widget.closest('.bsp_technical_interface_container') !== null;
          if (!isInsideTechnicalInterface) {
            // Find the trigger button inside this widget's shadow DOM
            const shadowRoot = widget.shadowRoot;
            if (shadowRoot) {
              // Try multiple selectors to find the trigger
              const trigger = shadowRoot.querySelector('.besper-widget-trigger') || 
                            shadowRoot.querySelector('[part="besper_widgetTrigger"]') ||
                            shadowRoot.querySelector('[class*="besper-widget-trigger"]');
              if (trigger) {
                if (isOpen) {
                  // Hide when technical interface is expanded - use setProperty with important
                  trigger.style.setProperty('display', 'none', 'important');
                  trigger.style.setProperty('visibility', 'hidden', 'important');
                  trigger.style.setProperty('opacity', '0', 'important');
                  trigger.style.setProperty('pointer-events', 'none', 'important');
                  // Also hide the button inside
                  const button = trigger.querySelector('.besper-chat-button') || trigger.querySelector('button');
                  if (button) {
                    button.style.setProperty('display', 'none', 'important');
                    button.style.setProperty('visibility', 'hidden', 'important');
                    button.style.setProperty('opacity', '0', 'important');
                    button.style.setProperty('pointer-events', 'none', 'important');
                  }
                } else {
                  // Restore when collapsed
                  trigger.style.removeProperty('display');
                  trigger.style.removeProperty('visibility');
                  trigger.style.removeProperty('opacity');
                  trigger.style.removeProperty('pointer-events');
                  const button = trigger.querySelector('.besper-chat-button') || trigger.querySelector('button');
                  if (button) {
                    button.style.removeProperty('display');
                    button.style.removeProperty('visibility');
                    button.style.removeProperty('opacity');
                    button.style.removeProperty('pointer-events');
                  }
                }
              }
            }
            // Also check for trigger in regular DOM (some SDK implementations)
            const regularTrigger = widget.querySelector('.besper-widget-trigger') || 
                                  widget.querySelector('[part="besper_widgetTrigger"]');
            if (regularTrigger) {
              if (isOpen) {
                regularTrigger.style.setProperty('display', 'none', 'important');
                regularTrigger.style.setProperty('visibility', 'hidden', 'important');
                regularTrigger.style.setProperty('opacity', '0', 'important');
                regularTrigger.style.setProperty('pointer-events', 'none', 'important');
                const button = regularTrigger.querySelector('.besper-chat-button') || regularTrigger.querySelector('button');
                if (button) {
                  button.style.setProperty('display', 'none', 'important');
                  button.style.setProperty('visibility', 'hidden', 'important');
                  button.style.setProperty('opacity', '0', 'important');
                  button.style.setProperty('pointer-events', 'none', 'important');
                }
              } else {
                regularTrigger.style.removeProperty('display');
                regularTrigger.style.removeProperty('visibility');
                regularTrigger.style.removeProperty('opacity');
                regularTrigger.style.removeProperty('pointer-events');
                const button = regularTrigger.querySelector('.besper-chat-button') || regularTrigger.querySelector('button');
                if (button) {
                  button.style.removeProperty('display');
                  button.style.removeProperty('visibility');
                  button.style.removeProperty('opacity');
                  button.style.removeProperty('pointer-events');
                }
              }
            }
          }
        }
      });
    };

    // Run immediately
    hideExternalWidgetTrigger();

    // Set up MutationObserver to watch for style attribute changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style' && isOpen) {
          // Style was changed, re-apply hiding
          hideExternalWidgetTrigger();
        }
      });
    });

    // Observe all besper-bot-widget elements for style changes
    const allWidgets = document.querySelectorAll('besper-bot-widget[bot-id="public_bot_external"]');
    allWidgets.forEach(widget => {
      const isInsideTechnicalInterface = widget.closest('.bsp_technical_interface_container') !== null;
      if (!isInsideTechnicalInterface) {
        const shadowRoot = widget.shadowRoot;
        if (shadowRoot) {
          const trigger = shadowRoot.querySelector('.besper-widget-trigger') || 
                        shadowRoot.querySelector('[part="besper_widgetTrigger"]');
          if (trigger) {
            observer.observe(trigger, { attributes: true, attributeFilter: ['style'] });
          }
        }
        const regularTrigger = widget.querySelector('.besper-widget-trigger') || 
                            widget.querySelector('[part="besper_widgetTrigger"]');
        if (regularTrigger) {
          observer.observe(regularTrigger, { attributes: true, attributeFilter: ['style'] });
        }
      }
    });

    // Also set up an interval to catch dynamically added elements and override SDK's style resets
    if (isOpen) {
      hideWidgetTriggerIntervalRef.current = setInterval(hideExternalWidgetTrigger, 50); // More frequent to override SDK
    } else {
      if (hideWidgetTriggerIntervalRef.current) {
        clearInterval(hideWidgetTriggerIntervalRef.current);
        hideWidgetTriggerIntervalRef.current = null;
      }
      // Restore visibility when collapsed
      hideExternalWidgetTrigger();
    }

    return () => {
      observer.disconnect();
      if (hideWidgetTriggerIntervalRef.current) {
        clearInterval(hideWidgetTriggerIntervalRef.current);
        hideWidgetTriggerIntervalRef.current = null;
      }
    };
  }, [isOpen]);

  // Pre-create session for technical assistant bot on page load (not when interface opens)
  useEffect(() => {
    if (sessionPreCreatedRef.current) return; // Only create once
    
    const preCreateSession = async () => {
      try {
        const client = getApiClient();
        const botId = 'technical_b-esper_assistant';
        const result = await client.post('/v1/chatbot/sessions', {
          resource_id: botId,
          implementation_type: 'react-hook', // Container mode for technical interface
          browser_language: navigator.language || 'en',
          origin: window.location.origin
        });
        if (result?.success) {
          console.log('✅ Pre-created session for technical assistant:', result.session_id || result.thread_id);
          sessionPreCreatedRef.current = true;
        }
      } catch (error) {
        // Silently fail - session will be created when needed
        console.debug('Session pre-creation failed (non-critical):', error);
      }
    };
    
    preCreateSession();
  }, []); // Run once on mount

  // Calculate metrics from network requests
  const calculatedMetrics = useMemo(() => {
    if (networkRequests.length === 0) {
      return {
        totalRequests: 0,
        errorRate: 0,
        avgResponseTime: 0,
        totalDataTransferred: 0,
        requestsByStatus: {},
        requestsByType: {},
        requestsByEndpoint: {},
        responseTimeDistribution: []
      };
    }

    const totalRequests = networkRequests.length;
    const errors = networkRequests.filter(r => r.status >= 400).length;
    const errorRate = totalRequests > 0 ? (errors / totalRequests) * 100 : 0;
    
    const responseTimes = networkRequests
      .filter(r => r.endTime && r.startTime)
      .map(r => r.endTime - r.startTime);
    const avgResponseTime = responseTimes.length > 0
      ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
      : 0;

    const totalDataTransferred = networkRequests.reduce((sum, r) => sum + (r.size || 0), 0);

    const requestsByStatus = networkRequests.reduce((acc, r) => {
      const status = Math.floor(r.status / 100) * 100;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const requestsByType = networkRequests.reduce((acc, r) => {
      acc[r.type] = (acc[r.type] || 0) + 1;
      return acc;
    }, {});

    // Calculate response time percentiles for performance analysis
    const sortedResponseTimes = [...responseTimes].sort((a, b) => a - b);
    const p50 = sortedResponseTimes.length > 0 ? sortedResponseTimes[Math.floor(sortedResponseTimes.length * 0.5)] : 0;
    const p95 = sortedResponseTimes.length > 0 ? sortedResponseTimes[Math.floor(sortedResponseTimes.length * 0.95)] : 0;
    const p99 = sortedResponseTimes.length > 0 ? sortedResponseTimes[Math.floor(sortedResponseTimes.length * 0.99)] : 0;
    const minResponseTime = sortedResponseTimes.length > 0 ? sortedResponseTimes[0] : 0;
    const maxResponseTime = sortedResponseTimes.length > 0 ? sortedResponseTimes[sortedResponseTimes.length - 1] : 0;

    // Group requests by endpoint (simplified URL) with latency stats
    const requestsByEndpoint = networkRequests.reduce((acc, r) => {
      let endpoint;
      try {
        const url = new URL(r.url);
        endpoint = url.pathname;
      } catch {
        endpoint = r.url.split('?')[0].substring(0, 50);
      }
      
      if (!acc[endpoint]) {
        acc[endpoint] = {
          count: 0,
          latencies: []
        };
      }
      
      acc[endpoint].count += 1;
      
      // Calculate latency (duration) for this request
      if (r.endTime && r.startTime) {
        const latency = r.endTime - r.startTime;
        acc[endpoint].latencies.push(latency);
      } else if (r.duration) {
        acc[endpoint].latencies.push(r.duration);
      }
      
      return acc;
    }, {});
    
    // Calculate latency stats for each endpoint
    Object.keys(requestsByEndpoint).forEach(endpoint => {
      const latencies = requestsByEndpoint[endpoint].latencies;
      if (latencies.length > 0) {
        const sorted = [...latencies].sort((a, b) => a - b);
        requestsByEndpoint[endpoint].avgLatency = latencies.reduce((a, b) => a + b, 0) / latencies.length;
        requestsByEndpoint[endpoint].minLatency = sorted[0];
        requestsByEndpoint[endpoint].maxLatency = sorted[sorted.length - 1];
      } else {
        requestsByEndpoint[endpoint].avgLatency = 0;
        requestsByEndpoint[endpoint].minLatency = 0;
        requestsByEndpoint[endpoint].maxLatency = 0;
      }
    });

    // Calculate success rate
    const successCount = networkRequests.filter(r => r.status >= 200 && r.status < 300).length;
    const successRate = totalRequests > 0 ? (successCount / totalRequests) * 100 : 0;

    return {
      totalRequests,
      errorRate,
      successRate,
      avgResponseTime,
      totalDataTransferred,
      requestsByStatus,
      requestsByType,
      requestsByEndpoint,
      responseTimeDistribution: responseTimes,
      responseTimePercentiles: { p50, p95, p99, min: minResponseTime, max: maxResponseTime }
    };
  }, [networkRequests]);

  // Intercept console logs
  // CRITICAL: Use global storage to prevent multiple Logger instances from creating circular references
  useEffect(() => {
    // Check if console methods are already overridden by another Logger instance
    if (window._bspLoggerConsoleOverridden) {
      // Another Logger instance already overrode console - don't override again
      return;
    }
    
    // Store original console methods globally (not per instance) to prevent circular references
    if (!window._bspOriginalConsole) {
      window._bspOriginalConsole = {
        log: console.log.bind(console),
        info: console.info.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console)
      };
    }
    
    originalConsoleRef.current = window._bspOriginalConsole;

    // Remove emojis and other Unicode symbols from log messages
    const removeEmojis = (text) => {
      if (!text) return text;
      const str = String(text);
      
      // Comprehensive emoji removal using a single regex pattern
      // This pattern matches all emoji and symbol ranges
      const emojiPattern = /[\u{1F300}-\u{1F9FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{FE00}-\u{FE0F}]|[\u{200D}]|[\u{20D0}-\u{20FF}]|[\u{2190}-\u{21FF}]|[\u{2200}-\u{22FF}]|[\u{2300}-\u{23FF}]|[\u{2400}-\u{24FF}]|[\u{2500}-\u{25FF}]|[\u{2B00}-\u{2BFF}]/gu;
      
      return str
        .replace(emojiPattern, '') // Remove all emojis and symbols
        .replace(/\s+/g, ' ') // Clean up multiple spaces
        .trim();
    };

    const parseLogMessage = (message, type) => {
      // Remove emojis from the message first
      const cleanedMessage = removeEmojis(String(message));
      
      // Parse structured log format: [ENV][LEVEL][MODULE] timestamp message {context}
      const structuredPattern = /\[([^\]]+)\]\[([^\]]+)\]\[([^\]]+)\]\s+([^\s]+)\s+(.+?)(?:\s+\{([^}]+)\})?$/;
      const match = cleanedMessage.match(structuredPattern);
      
      if (match) {
        const [, env, level, module, timestamp, msg, contextStr] = match;
        let context = {};
        if (contextStr) {
          try {
            // Parse context string like "key: value, key2: value2"
            contextStr.split(',').forEach(pair => {
              const [key, ...valueParts] = pair.split(':');
              if (key && valueParts.length > 0) {
                const value = valueParts.join(':').trim();
                try {
                  context[key.trim()] = JSON.parse(value);
                } catch {
                  context[key.trim()] = value;
                }
              }
            });
          } catch (e) {
            // If parsing fails, keep as string
            context = { raw: contextStr };
          }
        }
        return {
          environment: env,
          level: level.toLowerCase(),
          module,
          timestamp: timestamp,
          message: msg,
          context,
          raw: message
        };
      }
      
      // Fallback for unstructured logs
      return {
        environment: 'UNKNOWN',
        level: type,
        module: 'APP',
        timestamp: new Date().toISOString(),
        message: cleanedMessage,
        context: {},
        raw: cleanedMessage
      };
    };

    const addLogEntry = (message, type = 'log') => {
      // Prevent infinite loops by checking if we're already in a log call
      if (window._bspLoggerInLogCall) {
        return; // Skip if already processing a log
      }
      
      try {
        window._bspLoggerInLogCall = true;
        const parsed = parseLogMessage(String(message), type);
        const logEntry = {
          id: ++logCounterRef.current,
          ...parsed,
          type: parsed.level || type,
          timestamp: Date.now(),
          time: new Date().toLocaleTimeString(),
          fullMessage: removeEmojis(parsed.raw || String(message))
        };
        
        setLogs(prevLogs => {
          const newLogs = [logEntry, ...prevLogs];
          return newLogs.slice(0, 1000); // Keep last 1000 logs
        });
      } finally {
        window._bspLoggerInLogCall = false;
      }
    };

    // Safe stringify for console: avoids "Maximum call stack size exceeded" on circular refs
    const safeStringify = (arg) => {
      if (arg === null) return 'null';
      if (typeof arg !== 'object') return String(arg);
      try {
        return JSON.stringify(arg, null, 2);
      } catch (_) {
        try {
          return Object.prototype.toString.call(arg) + ' (circular or non-serializable)';
        } catch (_2) {
          return '[object]';
        }
      }
    };

    // For Error instances use only .name and .message (no .stack traversal) to avoid getters that could throw or recurse
    const safeErrorString = (arg) => {
      if (arg && typeof arg === 'object' && typeof arg.message === 'string') {
        const name = (typeof arg.name === 'string' ? arg.name : 'Error');
        const msg = String(arg.message).slice(0, 2000);
        return name + ': ' + msg;
      }
      return null;
    };

    // Only override if not already overridden
    if (!window._bspLoggerConsoleOverridden) {
      const originalLog = window._bspOriginalConsole.log;
      const originalInfo = window._bspOriginalConsole.info;
      const originalWarn = window._bspOriginalConsole.warn;
      const originalError = window._bspOriginalConsole.error;
      
      console.log = (...args) => {
        originalLog(...args);
        try {
          const message = args.map(arg => safeStringify(arg)).join(' ');
          addLogEntry(message, 'log');
        } catch (_) { /* no-op to avoid recursion */ }
      };

      console.info = (...args) => {
        originalInfo(...args);
        try {
          const message = args.map(arg => safeStringify(arg)).join(' ');
          addLogEntry(message, 'info');
        } catch (_) { /* no-op */ }
      };

      console.warn = (...args) => {
        originalWarn(...args);
        try {
          const message = args.map(arg => safeStringify(arg)).join(' ');
          addLogEntry(message, 'warn');
        } catch (_) { /* no-op */ }
      };

      console.error = (...args) => {
        originalError(...args);
        try {
          // Prefer shallow Error formatting to avoid getters/circular refs that could recurse
          const parts = args.map((arg) => {
            const errStr = safeErrorString(arg);
            if (errStr !== null) return errStr;
            return safeStringify(arg);
          });
          const message = parts.join(' ');
          addLogEntry(message.length > 3000 ? message.slice(0, 3000) + '…' : message, 'error');
        } catch (_) {
          try { addLogEntry('console.error failed to format args', 'error'); } catch (_2) {}
        }
      };
      
      // Mark as overridden
      window._bspLoggerConsoleOverridden = true;
    }

    // Intercept fetch
    if (window.fetch && !window._originalFetch) {
      window._originalFetch = window.fetch;
      window.fetch = function(resource, options = {}) {
        const startTime = performance.now();
        const url = typeof resource === 'string' ? resource : resource.url;
        const method = options.method || 'GET';
        let requestHeaders = {};
        let requestBody = null;
        
        try {
          requestHeaders = options.headers ? Object.fromEntries(new Headers(options.headers).entries()) : {};
          requestBody = options.body ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body)) : null;
        } catch (e) {
          // If header/body parsing fails, continue without them
        }
        
        return window._originalFetch.apply(this, arguments)
          .then(response => {
            const endTime = performance.now();
            const duration = endTime - startTime;
            const responseHeaders = {};
            try {
              response.headers.forEach((value, key) => {
                responseHeaders[key] = value;
              });
            } catch (e) {
              // If header parsing fails, continue without headers
            }
            
            response.clone().text().then(text => {
              const size = text.length;
              let responseData = null;
              try {
                responseData = JSON.parse(text);
              } catch {
                responseData = text.substring(0, 200); // First 200 chars for non-JSON
              }
              
              addNetworkRequest({
                id: Date.now() + Math.random(),
                url,
                method,
                type: 'fetch',
                startTime: Date.now() - duration,
                endTime: Date.now(),
                status: response.status,
                statusText: response.statusText,
                size,
                duration,
                requestHeaders,
                responseHeaders,
                requestBody,
                responseBody: responseData,
                timestamp: new Date().toISOString()
              });
            }).catch(() => {
              addNetworkRequest({
                id: Date.now() + Math.random(),
                url,
                method,
                type: 'fetch',
                startTime: Date.now() - duration,
                endTime: Date.now(),
                status: response.status,
                statusText: response.statusText,
                size: 0,
                duration,
                requestHeaders,
                responseHeaders: {},
                requestBody,
                timestamp: new Date().toISOString()
              });
            });
            
            return response;
          })
          .catch(error => {
            const endTime = performance.now();
            const duration = endTime - startTime;
            addNetworkRequest({
              id: Date.now() + Math.random(),
              url,
              method,
              type: 'fetch',
              startTime: Date.now() - duration,
              endTime: Date.now(),
              status: 0,
              statusText: 'Network Error',
              size: 0,
              duration,
              error: error.message,
              requestHeaders,
              requestBody,
              timestamp: new Date().toISOString()
            });
            throw error;
          });
      };
    }

    const addNetworkRequest = (request) => {
      setNetworkRequests(prev => {
        const newRequests = [request, ...prev];
        return newRequests.slice(0, 2000); // Keep last 2000 requests
      });
    };

    return () => {
      // Only restore if this is the last Logger instance
      // Check if there are other Logger instances still mounted
      const loggerInstances = document.querySelectorAll('.bsp_technical_interface_container');
      if (loggerInstances.length <= 1 && window._bspOriginalConsole) {
        // This is the last instance, restore original console methods
        console.log = window._bspOriginalConsole.log;
        console.info = window._bspOriginalConsole.info;
        console.warn = window._bspOriginalConsole.warn;
        console.error = window._bspOriginalConsole.error;
        window._bspLoggerConsoleOverridden = false;
      }
      if (window._originalFetch) {
        window.fetch = window._originalFetch;
        delete window._originalFetch;
      }
    };
  }, []);

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let value = bytes;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    return `${value.toFixed(2)} ${units[unitIndex]}`;
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      return 'Invalid date';
    }
    
    if (isNaN(date.getTime())) return 'Invalid date';
    
    // Format: "Jan 15, 2026 at 9:47:27 AM"
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    
    const formatted = date.toLocaleString('en-US', options);
    const isoString = date.toISOString();
    
    return (
      <span title={isoString} style={{ fontFamily: 'monospace' }}>
        <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{formatted}</span>
        <span style={{ color: '#9ca3af', fontSize: '0.85em', marginLeft: '8px' }}>
          ({isoString})
        </span>
      </span>
    );
  };

  const formatHeaderValue = (key, value) => {
    const strValue = String(value);
    
    // Format authorization tokens
    if (key.toLowerCase() === 'authorization' && strValue.startsWith('Bearer ')) {
      const token = strValue.substring(7);
      if (token.length > 60) {
        return (
          <span title={strValue} style={{ fontFamily: 'monospace' }}>
            <span style={{ color: '#60a5fa' }}>Bearer</span>
            <span style={{ color: '#9ca3af', marginLeft: '8px' }}>
              {token.substring(0, 30)}...{token.substring(token.length - 10)}
            </span>
            <span style={{ color: '#6b7280', fontSize: '0.85em', marginLeft: '8px' }}>
              ({token.length} chars)
            </span>
          </span>
        );
      }
      return (
        <span style={{ fontFamily: 'monospace' }}>
          <span style={{ color: '#60a5fa' }}>Bearer</span>
          <span style={{ color: '#9ca3af', marginLeft: '8px' }}>{token}</span>
        </span>
      );
    }
    
    // Format request IDs (e.g., "req_1768466847783_ij19fs2g1")
    if (key.toLowerCase().includes('request-id') || key.toLowerCase().includes('requestid')) {
      const parts = strValue.split('_');
      if (parts.length >= 3) {
        const [prefix, timestamp, id] = parts;
        const date = new Date(parseInt(timestamp));
        const formattedDate = !isNaN(date.getTime()) 
          ? date.toLocaleString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              hour: 'numeric', 
              minute: '2-digit', 
              second: '2-digit',
              hour12: true 
            })
          : timestamp;
        
        return (
          <span title={strValue} style={{ fontFamily: 'monospace' }}>
            <span style={{ color: '#60a5fa', fontWeight: 500 }}>{prefix}</span>
            <span style={{ color: '#9ca3af', marginLeft: '6px' }}>_</span>
            <span style={{ color: '#e2e8f0' }}>{formattedDate}</span>
            <span style={{ color: '#9ca3af', marginLeft: '6px' }}>_</span>
            <span style={{ color: '#a78bfa' }}>{id}</span>
          </span>
        );
      }
    }
    
    // Format long URLs
    if (key.toLowerCase().includes('url') && strValue.length > 80) {
      return (
        <span title={strValue} style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
          <span style={{ color: '#e2e8f0' }}>{strValue.substring(0, 60)}</span>
          <span style={{ color: '#6b7280' }}>...</span>
          <span style={{ color: '#9ca3af', fontSize: '0.85em', marginLeft: '8px' }}>
            ({strValue.length} chars)
          </span>
        </span>
      );
    }
    
    // Format long strings (truncate if too long)
    if (strValue.length > 100) {
      return (
        <span title={strValue} style={{ fontFamily: 'monospace' }}>
          <span style={{ color: '#e2e8f0' }}>{strValue.substring(0, 80)}</span>
          <span style={{ color: '#6b7280' }}>...</span>
          <span style={{ color: '#9ca3af', fontSize: '0.85em', marginLeft: '8px' }}>
            ({strValue.length} chars)
          </span>
        </span>
      );
    }
    
    // Default formatting
    return (
      <span style={{ fontFamily: 'monospace', color: '#e2e8f0' }}>
        {strValue}
      </span>
    );
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return '#10b981';
    if (status >= 300 && status < 400) return '#3b82f6';
    if (status >= 400 && status < 500) return '#f59e0b';
    if (status >= 500) return '#ef4444';
    return '#6b7280';
  };

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // Filter by time range
      const logTime = log.timestamp;
      const now = Date.now();
      const ranges = {
        '15m': 15 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        'all': Infinity
      };
      const inTimeRange = (now - logTime) <= (ranges[timeRange] || Infinity);
      
      // Filter by type
      const matchesType = logFilter === 'all' || log.type === logFilter;
      
      // Filter by search
      const matchesSearch = !logSearch || 
        log.message.toLowerCase().includes(logSearch.toLowerCase()) ||
        log.module?.toLowerCase().includes(logSearch.toLowerCase()) ||
        log.fullMessage?.toLowerCase().includes(logSearch.toLowerCase());
      
      return inTimeRange && matchesType && matchesSearch;
    });
  }, [logs, timeRange, logFilter, logSearch]);

  const sortedAndFilteredNetworkRequests = useMemo(() => {
    let filtered = networkRequests.filter(req => {
      const reqTime = req.startTime || req.endTime || 0;
      const now = Date.now();
      const ranges = {
        '15m': 15 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        'all': Infinity
      };
      const inTimeRange = (now - reqTime) <= (ranges[timeRange] || Infinity);
      
      // Filter by search
      const matchesSearch = !networkSearch ||
        req.url.toLowerCase().includes(networkSearch.toLowerCase()) ||
        req.method.toLowerCase().includes(networkSearch.toLowerCase()) ||
        String(req.status).includes(networkSearch);
      
      return inTimeRange && matchesSearch;
    });
    
    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'duration') {
          aVal = a.duration || 0;
          bVal = b.duration || 0;
        } else if (sortConfig.key === 'status') {
          aVal = a.status || 0;
          bVal = b.status || 0;
        } else if (sortConfig.key === 'timestamp') {
          aVal = a.startTime || a.endTime || 0;
          bVal = b.startTime || b.endTime || 0;
        }
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [networkRequests, timeRange, networkSearch, sortConfig]);
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (sidebarOpen) return null;

  const toggleLogger = () => setIsOpen(!isOpen);

  return (
    <div className={`bsp_technical_interface_container ${isOpen ? 'bsp_technical_interface_expanded' : ''}`} id="bsp_technical_interface_container">
      {/* Header */}
      <div className={`bsp_technical_interface_header ${isOpen ? 'bsp_technical_interface_expanded' : ''}`} onClick={toggleLogger}>
        <div className="bsp_technical_interface_header_left">
          <svg className="bsp_technical_interface_terminal_icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <polyline points="4,17 10,11 4,5"></polyline>
            <line x1="12" y1="19" x2="20" y2="19"></line>
          </svg>
          <svg className={`bsp_technical_interface_chevron_icon ${isOpen ? 'bsp_technical_interface_open' : ''}`} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </div>
        {isOpen && (
          <div className="bsp_technical_interface_header_right">
            <span style={{ color: 'white', fontSize: '12px', marginRight: '8px' }}>{calculatedMetrics.totalRequests} requests</span>
          </div>
        )}
      </div>

      {/* Content Area */}
      {isOpen && (
        <div className={`bsp_technical_interface_content ${isOpen ? 'bsp_technical_interface_open' : ''}`}>
          {/* Main Tab Navigation */}
          <div className="bsp-main-tabs">
            <button
              className={`bsp-main-tab ${mainTab === 'search' ? 'active' : ''}`}
              onClick={() => setMainTab('search')}
            >
              Search
            </button>
            <button
              className={`bsp-main-tab ${mainTab === 'besper-bot' ? 'active' : ''}`}
              onClick={() => setMainTab('besper-bot')}
            >
              B-esper Bot
            </button>
            <button
              className={`bsp-main-tab ${mainTab === 'whats-besper' ? 'active' : ''}`}
              onClick={() => setMainTab('whats-besper')}
            >
              What's B-esper
            </button>
            <button
              className={`bsp-main-tab ${mainTab === 'browser' ? 'active' : ''}`}
              onClick={() => setMainTab('browser')}
            >
              Browser
            </button>
          </div>

          {/* Search Tab */}
          {mainTab === 'search' && (
            <div className="bsp-bot-tab-content">
              <SearchTab />
            </div>
          )}

          {/* Sub-tab Navigation and Content */}
          {mainTab === 'browser' && (
            <>
              {/* Browser Sub-tabs */}
              <div className="bsp-sub-tabs">
                <button
                  className={`bsp-sub-tab ${browserSubTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setBrowserSubTab('overview')}
                >
                  Overview
                </button>
                <button
                  className={`bsp-sub-tab ${browserSubTab === 'network' ? 'active' : ''}`}
                  onClick={() => setBrowserSubTab('network')}
                >
                  Network
                </button>
                <button
                  className={`bsp-sub-tab ${browserSubTab === 'logs' ? 'active' : ''}`}
                  onClick={() => setBrowserSubTab('logs')}
                >
                  Logs
                </button>
              </div>

              {/* Time Range Filter */}
              <div className="bsp-analytics-filters">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="bsp-analytics-time-filter"
                >
                  <option value="15m">Last 15 minutes</option>
                  <option value="1h">Last hour</option>
                  <option value="24h">Last 24 hours</option>
                  <option value="all">All time</option>
                </select>
              </div>

              {/* Browser Tab Content */}
              <div className="bsp-analytics-tab-content">
                {browserSubTab === 'overview' && (
              <div className="bsp-analytics-overview">
                {/* Metrics Cards */}
                <div className="bsp-analytics-metrics-grid">
                  <div className="bsp-analytics-metric-card">
                    <div className="bsp-analytics-metric-label">Total Requests</div>
                    <div className="bsp-analytics-metric-value">{calculatedMetrics.totalRequests}</div>
                  </div>
                  <div className="bsp-analytics-metric-card">
                    <div className="bsp-analytics-metric-label">Error Rate</div>
                    <div className="bsp-analytics-metric-value" style={{ color: calculatedMetrics.errorRate > 5 ? '#ef4444' : '#10b981' }}>
                      {calculatedMetrics.errorRate.toFixed(2)}%
                    </div>
                  </div>
                  <div className="bsp-analytics-metric-card">
                    <div className="bsp-analytics-metric-label">Avg Response Time</div>
                    <div className="bsp-analytics-metric-value">{formatDuration(calculatedMetrics.avgResponseTime)}</div>
                  </div>
                  <div className="bsp-analytics-metric-card">
                    <div className="bsp-analytics-metric-label">Data Transferred</div>
                    <div className="bsp-analytics-metric-value">{formatSize(calculatedMetrics.totalDataTransferred)}</div>
                  </div>
                  <div className="bsp-analytics-metric-card">
                    <div className="bsp-analytics-metric-label">Success Rate</div>
                    <div className="bsp-analytics-metric-value" style={{ color: calculatedMetrics.successRate >= 95 ? '#10b981' : calculatedMetrics.successRate >= 90 ? '#f59e0b' : '#ef4444' }}>
                      {calculatedMetrics.successRate.toFixed(1)}%
                    </div>
                  </div>
                  <div className="bsp-analytics-metric-card">
                    <div className="bsp-analytics-metric-label">P95 Response Time</div>
                    <div className="bsp-analytics-metric-value">{formatDuration(calculatedMetrics.responseTimePercentiles?.p95 || 0)}</div>
                  </div>
                </div>

                {/* Status Distribution Table */}
                <div className="bsp-analytics-table-section">
                  <h3 className="bsp-analytics-section-title">Requests by Status</h3>
                  <table className="bsp-analytics-table">
                    <thead>
                      <tr>
                        <th>Status Code</th>
                        <th>Count</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(calculatedMetrics.requestsByStatus)
                        .sort(([a], [b]) => parseInt(b) - parseInt(a))
                        .map(([status, count]) => (
                          <tr key={status}>
                            <td>
                              <span
                                className="bsp-analytics-status-badge"
                                style={{ backgroundColor: getStatusColor(parseInt(status)) }}
                              >
                                {status}
                              </span>
                            </td>
                            <td>{count}</td>
                            <td>{((count / calculatedMetrics.totalRequests) * 100).toFixed(1)}%</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Request Type Distribution */}
                <div className="bsp-analytics-table-section">
                  <h3 className="bsp-analytics-section-title">Requests by Type</h3>
                  <table className="bsp-analytics-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Count</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(calculatedMetrics.requestsByType)
                        .sort(([, a], [, b]) => b - a)
                        .map(([type, count]) => (
                          <tr key={type}>
                            <td>{type}</td>
                            <td>{count}</td>
                            <td>{((count / calculatedMetrics.totalRequests) * 100).toFixed(1)}%</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Performance Metrics Table */}
                <div className="bsp-analytics-table-section">
                  <h3 className="bsp-analytics-section-title">Performance Metrics</h3>
                  <table className="bsp-analytics-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th>Value</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Success Rate</td>
                        <td style={{ color: calculatedMetrics.successRate >= 95 ? '#10b981' : calculatedMetrics.successRate >= 90 ? '#f59e0b' : '#ef4444' }}>
                          {calculatedMetrics.successRate.toFixed(2)}%
                        </td>
                        <td>Percentage of successful requests (2xx)</td>
                      </tr>
                      <tr>
                        <td>Average Response Time</td>
                        <td>{formatDuration(calculatedMetrics.avgResponseTime)}</td>
                        <td>Mean response time across all requests</td>
                      </tr>
                      <tr>
                        <td>P50 (Median)</td>
                        <td>{formatDuration(calculatedMetrics.responseTimePercentiles.p50)}</td>
                        <td>50th percentile response time</td>
                      </tr>
                      <tr>
                        <td>P95</td>
                        <td>{formatDuration(calculatedMetrics.responseTimePercentiles.p95)}</td>
                        <td>95th percentile response time</td>
                      </tr>
                      <tr>
                        <td>P99</td>
                        <td>{formatDuration(calculatedMetrics.responseTimePercentiles.p99)}</td>
                        <td>99th percentile response time</td>
                      </tr>
                      <tr>
                        <td>Min Response Time</td>
                        <td>{formatDuration(calculatedMetrics.responseTimePercentiles.min)}</td>
                        <td>Fastest request</td>
                      </tr>
                      <tr>
                        <td>Max Response Time</td>
                        <td>{formatDuration(calculatedMetrics.responseTimePercentiles.max)}</td>
                        <td>Slowest request</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Top Endpoints Table */}
                {Object.keys(calculatedMetrics.requestsByEndpoint).length > 0 && (
                  <div className="bsp-analytics-table-section">
                    <h3 className="bsp-analytics-section-title">Top Endpoints</h3>
                    <table className="bsp-analytics-table">
                      <thead>
                        <tr>
                          <th>Endpoint</th>
                          <th>Request Count</th>
                          <th>Percentage</th>
                          <th>Avg Latency</th>
                          <th>Min Latency</th>
                          <th>Max Latency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(calculatedMetrics.requestsByEndpoint)
                          .sort(([, a], [, b]) => b.count - a.count)
                          .slice(0, 10)
                          .map(([endpoint, stats]) => (
                            <tr key={endpoint}>
                              <td className="bsp-analytics-url-cell" title={endpoint}>
                                {endpoint.length > 60 ? `${endpoint.substring(0, 60)}...` : endpoint}
                              </td>
                              <td>{stats.count}</td>
                              <td>{((stats.count / calculatedMetrics.totalRequests) * 100).toFixed(1)}%</td>
                              <td>{formatDuration(stats.avgLatency || 0)}</td>
                              <td>{formatDuration(stats.minLatency || 0)}</td>
                              <td>{formatDuration(stats.maxLatency || 0)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
                </div>
                )}

                {browserSubTab === 'network' && (
              <div className="bsp-analytics-network">
                <div className="bsp-analytics-table-controls">
                  <input
                    type="text"
                    placeholder="Search requests (URL, method, status)..."
                    value={networkSearch}
                    onChange={(e) => setNetworkSearch(e.target.value)}
                    className="bsp-analytics-search-input"
                  />
                  <button
                    onClick={() => setNetworkRequests([])}
                    className="bsp-analytics-clear-btn"
                  >
                    Clear All
                  </button>
                </div>
                <div className="bsp-analytics-table-wrapper">
                  <table className="bsp-analytics-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('timestamp')} className="bsp-analytics-sortable">
                          Time {sortConfig.key === 'timestamp' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSort('method')} className="bsp-analytics-sortable">
                          Method {sortConfig.key === 'method' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th>URL</th>
                        <th onClick={() => handleSort('status')} className="bsp-analytics-sortable">
                          Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSort('duration')} className="bsp-analytics-sortable">
                          Duration {sortConfig.key === 'duration' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th>Size</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAndFilteredNetworkRequests.slice(0, 200).map((req, idx) => (
                        <React.Fragment key={req.id || idx}>
                          <tr 
                            className={selectedRequest?.id === req.id ? 'bsp-analytics-row-selected' : ''}
                            onClick={() => setSelectedRequest(selectedRequest?.id === req.id ? null : req)}
                          >
                            <td>{new Date(req.startTime || req.endTime || req.timestamp).toLocaleTimeString()}</td>
                            <td><span className="bsp-analytics-method-badge">{req.method}</span></td>
                            <td className="bsp-analytics-url-cell" title={req.url}>
                              {req.url.length > 50 ? `${req.url.substring(0, 50)}...` : req.url}
                            </td>
                            <td>
                              <span
                                className="bsp-analytics-status-badge"
                                style={{ backgroundColor: getStatusColor(req.status) }}
                              >
                                {req.status || 'N/A'}
                              </span>
                            </td>
                            <td>{formatDuration(req.duration || 0)}</td>
                            <td>{formatSize(req.size || 0)}</td>
                            <td>
                              <button
                                className="bsp-analytics-detail-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRequest(selectedRequest?.id === req.id ? null : req);
                                }}
                              >
                                {selectedRequest?.id === req.id ? 'Hide' : 'Details'}
                              </button>
                            </td>
                          </tr>
                          {selectedRequest?.id === req.id && (
                            <tr className="bsp-analytics-detail-row">
                              <td colSpan="7">
                                <div className="bsp-analytics-request-details">
                                  <div className="bsp-analytics-detail-section">
                                    <h4>Request Details</h4>
                                    <table className="bsp-analytics-detail-table">
                                      <tbody>
                                        <tr><td>URL</td><td>{formatHeaderValue('url', req.url)}</td></tr>
                                        <tr>
                                          <td>Method</td>
                                          <td>
                                            <span style={{ fontFamily: 'monospace' }}>
                                              <span style={{ 
                                                color: '#60a5fa', 
                                                fontWeight: 600,
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: 'rgba(96, 165, 250, 0.1)'
                                              }}>
                                                {req.method}
                                              </span>
                                            </span>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>Status</td>
                                          <td>
                                            <span style={{ fontFamily: 'monospace' }}>
                                              <span style={{ 
                                                color: getStatusColor(req.status) === '#10b981' ? '#10b981' : 
                                                       getStatusColor(req.status) === '#f59e0b' ? '#f59e0b' :
                                                       getStatusColor(req.status) === '#ef4444' ? '#ef4444' : '#60a5fa',
                                                fontWeight: 600,
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: getStatusColor(req.status) === '#10b981' ? 'rgba(16, 185, 129, 0.1)' :
                                                               getStatusColor(req.status) === '#f59e0b' ? 'rgba(245, 158, 11, 0.1)' :
                                                               getStatusColor(req.status) === '#ef4444' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(96, 165, 250, 0.1)'
                                              }}>
                                                {req.status}
                                              </span>
                                              {req.statusText && (
                                                <span style={{ color: '#9ca3af', marginLeft: '8px' }}>
                                                  {req.statusText}
                                                </span>
                                              )}
                                            </span>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>Duration</td>
                                          <td>
                                            <span style={{ fontFamily: 'monospace', color: '#e2e8f0', fontWeight: 500 }}>
                                              {formatDuration(req.duration || 0)}
                                            </span>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>Size</td>
                                          <td>
                                            <span style={{ fontFamily: 'monospace', color: '#e2e8f0', fontWeight: 500 }}>
                                              {formatSize(req.size || 0)}
                                            </span>
                                          </td>
                                        </tr>
                                        <tr><td>Timestamp</td><td>{formatTimestamp(req.timestamp || req.startTime || req.endTime)}</td></tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  {req.requestHeaders && Object.keys(req.requestHeaders).length > 0 && (
                                    <div className="bsp-analytics-detail-section">
                                      <h4>Request Headers</h4>
                                      <table className="bsp-analytics-detail-table">
                                        <tbody>
                                          {Object.entries(req.requestHeaders).map(([key, value]) => (
                                            <tr key={key}>
                                              <td style={{ textTransform: 'capitalize' }}>{key.replace(/-/g, ' ')}</td>
                                              <td>{formatHeaderValue(key, value)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  {req.requestBody && (
                                    <div className="bsp-analytics-detail-section">
                                      <h4>Request Body</h4>
                                      <pre className="bsp-analytics-code-block">{typeof req.requestBody === 'string' ? req.requestBody : JSON.stringify(req.requestBody, null, 2)}</pre>
                                    </div>
                                  )}
                                  {req.responseHeaders && Object.keys(req.responseHeaders).length > 0 && (
                                    <div className="bsp-analytics-detail-section">
                                      <h4>Response Headers</h4>
                                      <table className="bsp-analytics-detail-table">
                                        <tbody>
                                          {Object.entries(req.responseHeaders).map(([key, value]) => (
                                            <tr key={key}>
                                              <td style={{ textTransform: 'capitalize' }}>{key.replace(/-/g, ' ')}</td>
                                              <td>{formatHeaderValue(key, value)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  {req.responseBody && (
                                    <div className="bsp-analytics-detail-section">
                                      <h4>Response Body</h4>
                                      <pre className="bsp-analytics-code-block">{typeof req.responseBody === 'string' ? req.responseBody : JSON.stringify(req.responseBody, null, 2)}</pre>
                                    </div>
                                  )}
                                  {req.error && (
                                    <div className="bsp-analytics-detail-section">
                                      <h4>Error</h4>
                                      <pre className="bsp-analytics-code-block bsp-analytics-error">{req.error}</pre>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {sortedAndFilteredNetworkRequests.length === 0 && (
                  <div className="bsp-analytics-empty">No network requests in selected time range</div>
                )}
                </div>
                )}

                {browserSubTab === 'logs' && (
              <div className="bsp-analytics-logs">
                <div className="bsp-analytics-table-controls">
                  <input
                    type="text"
                    placeholder="Search logs (message, module)..."
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    className="bsp-analytics-search-input"
                  />
                  <select
                    value={logFilter}
                    onChange={(e) => setLogFilter(e.target.value)}
                    className="bsp-analytics-filter-select"
                  >
                    <option value="all">All Levels</option>
                    <option value="error">Errors</option>
                    <option value="warn">Warnings</option>
                    <option value="info">Info</option>
                    <option value="log">Logs</option>
                    <option value="debug">Debug</option>
                  </select>
                  <button
                    onClick={() => setLogs([])}
                    className="bsp-analytics-clear-btn"
                  >
                    Clear All
                  </button>
                </div>
                <div className="bsp-analytics-table-wrapper">
                  <table className="bsp-analytics-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Level</th>
                        <th>Module</th>
                        <th>Message</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.slice(0, 500).map((log) => (
                        <React.Fragment key={log.id}>
                          <tr 
                            className={`bsp-analytics-log-row bsp-analytics-log-${log.type} ${selectedLog?.id === log.id ? 'bsp-analytics-row-selected' : ''}`}
                            onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                          >
                            <td>{log.time}</td>
                            <td>
                              <span className={`bsp-analytics-log-level bsp-analytics-log-level-${log.type}`}>
                                {log.type.toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <span className="bsp-analytics-module-badge">{log.module || 'APP'}</span>
                            </td>
                            <td className="bsp-analytics-log-message" title={log.fullMessage}>
                              {log.message.length > 80 ? `${log.message.substring(0, 80)}...` : log.message}
                            </td>
                            <td>
                              <button
                                className="bsp-analytics-detail-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLog(selectedLog?.id === log.id ? null : log);
                                }}
                              >
                                {selectedLog?.id === log.id ? 'Hide' : 'Details'}
                              </button>
                            </td>
                          </tr>
                          {selectedLog?.id === log.id && (
                            <tr className="bsp-analytics-detail-row">
                              <td colSpan="5">
                                <div className="bsp-analytics-log-details">
                                  <div className="bsp-analytics-detail-section">
                                    <h4>Log Details</h4>
                                    <table className="bsp-analytics-detail-table">
                                      <tbody>
                                        <tr><td>Time</td><td>{formatTimestamp(log.timestamp)}</td></tr>
                                        <tr><td>Level</td><td><code>{log.type.toUpperCase()}</code></td></tr>
                                        <tr><td>Module</td><td><code>{log.module || 'APP'}</code></td></tr>
                                        <tr><td>Environment</td><td><code>{log.environment || 'UNKNOWN'}</code></td></tr>
                                        <tr><td>Message</td><td><code>{log.message}</code></td></tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  {log.context && Object.keys(log.context).length > 0 && (
                                    <div className="bsp-analytics-detail-section">
                                      <h4>Context</h4>
                                      <table className="bsp-analytics-detail-table">
                                        <tbody>
                                          {Object.entries(log.context).map(([key, value]) => (
                                            <tr key={key}>
                                              <td>{key}</td>
                                              <td><code>{typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}</code></td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  <div className="bsp-analytics-detail-section">
                                    <h4>Full Message</h4>
                                    <pre className="bsp-analytics-code-block">{log.fullMessage || log.message}</pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredLogs.length === 0 && (
                  <div className="bsp-analytics-empty">No logs in selected time range</div>
                )}
                </div>
                )}
              </div>
              </>
            )}

            {mainTab === 'besper-bot' && (
              <>
                {/* B-esper Bot Sub-tabs */}
                <div className="bsp-sub-tabs">
                  <button
                    className={`bsp-sub-tab ${botSubTab === 'assistant' ? 'active' : ''}`}
                    onClick={() => setBotSubTab('assistant')}
                  >
                    Assistant
                  </button>
                  <button
                    className={`bsp-sub-tab ${botSubTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setBotSubTab('upcoming')}
                  >
                    Upcoming
                  </button>
                </div>

                {/* B-esper Bot Tab Content */}
                <div className="bsp-bot-tab-content">
                  {botSubTab === 'assistant' && (
                    <BotAssistantTab />
                  )}
                  {botSubTab === 'upcoming' && (
                    <BotUpcomingTab />
                  )}
                </div>
              </>
            )}

            {mainTab === 'whats-besper' && (
              <WhatBESperTab />
            )}
        </div>
      )}
    </div>
  );
};

// Color constants matching BotChatContainer
const DARK_MODE_COLORS = {
  sidebarBg: '#2d3748',
  sidebarHeaderBg: '#2d3748',
  chatBg: '#1a202c',
  chatHeaderBg: '#2d3748',
  bgPrimary: '#1a202c',
  bgSecondary: '#2d3748',
  textPrimary: '#e2e8f0',
  textSecondary: '#94a3b8',
  textTertiary: '#718096',
  textMuted: '#a0aec0',
  textInverse: '#ffffff',
  textHeader: '#e2e8f0',
  textHeaderAlt: '#e2e8f0',
  userBubbleBg: '#2d3748',
  userBubbleText: '#e2e8f0',
  botBubbleBg: '#2d3748',
  botBubbleText: '#e2e8f0',
  border: '#4a5568',
  borderFocus: '#ff8c42',
  primary: '#ff8c42',
  primaryHover: '#ff7700',
  success: '#38a169',
  error: '#e53e3e',
  radiusLg: '8px',
  containerBorderRadius: '8px',
};

// eslint-disable-next-line no-unused-vars -- reserved for theme switching
const LIGHT_MODE_COLORS = {
  sidebarBg: '#ffffff',
  sidebarHeaderBg: '#ffffff',
  chatBg: '#ffffff',
  chatHeaderBg: '#ffffff',
  bgPrimary: '#ffffff',
  bgSecondary: '#f7fafc',
  textPrimary: '#2d3748',
  textSecondary: '#4a5568',
  textTertiary: '#718096',
  textMuted: '#a0aec0',
  textInverse: '#ffffff',
  textHeader: '#1a202c',
  textHeaderAlt: '#333333',
  userBubbleBg: '#5897de',
  userBubbleText: '#ffffff',
  botBubbleBg: '#ffffff',
  botBubbleText: '#1a202c',
  border: '#e2e8f0',
  borderFocus: '#5897de',
  primary: '#5897de',
  primaryHover: '#4a7fc4',
  success: '#38a169',
  error: '#e53e3e',
  radiusLg: '8px',
  containerBorderRadius: '8px',
};

// Bot Assistant Tab Component
const BotAssistantTab = () => {
  // Technical interface is always dark - hardcode dark mode for SDK container
  const [apiDomain, setApiDomain] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);
  const sdkInstanceRef = useRef(null);
  const SDK_CONTAINER_ID = 'bsp-tech-assistant-container';
  const botId = 'technical_b-esper_assistant';

  useEffect(() => {
    const loadApiDomain = async () => {
      try {
        const endpoint = await getApiEndpoint('public');
        const url = new URL(endpoint);
        setApiDomain(url.origin);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setIsLoading(false);
      }
    };
    loadApiDomain();
  }, []);

  // Initialize SDK
  useEffect(() => {
    if (!apiDomain || !containerRef.current) return;

    const initializeSDK = async () => {
      if (sdkInstanceRef.current) return;
      
      // Ensure containerRef is available
      if (!containerRef.current) {
        console.warn('Container ref not available for SDK initialization');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const scriptUrl = buildSdkUrl(apiDomain, 'bots', botId, 'container');
        containerRef.current.id = SDK_CONTAINER_ID;

        // Check if CONTAINER SDK is already loaded (NOT widget SDK)
        const existingContainerScript = document.querySelector(`script[src*="/api/v1/sdk/bots/${botId}/container"]`);
        // Also check if widget SDK is incorrectly loaded - we need to prevent that
        const existingWidgetScript = document.querySelector(`script[src*="/api/v1/sdk/bots/${botId}/widget"]`);
        
        // If widget SDK is loaded, remove it - we only want container SDK
        if (existingWidgetScript && !existingContainerScript) {
          console.warn('⚠️ Widget SDK detected for technical interface - removing and loading container SDK instead');
          existingWidgetScript.remove();
        }
        
        // Only proceed if container SDK custom element is available
        if (existingContainerScript && customElements.get('besper-bot-container')) {
          if (!containerRef.current) {
            setIsLoading(false);
            return;
          }
          // Check for container element first (container mode) - this is what we want
          const existingContainer = containerRef.current.querySelector('besper-bot-container[bot-id="' + botId + '"]');
          if (existingContainer) {
            // Verify it's the correct bot
            if (existingContainer.getAttribute('bot-id') === botId) {
              existingContainer.setAttribute('darkmode', 'true');
              sdkInstanceRef.current = { destroy: () => existingContainer.remove() };
              setIsLoading(false);
              return;
            }
          }
          
          // Remove any widget elements that might have been created incorrectly
          const incorrectWidget = containerRef.current.querySelector('besper-bot-widget');
          if (incorrectWidget) {
            console.warn('⚠️ Removing incorrectly created widget element, creating container instead');
            incorrectWidget.remove();
          }

          // Create container element for container mode
          const containerElement = document.createElement('besper-bot-container');
          containerElement.setAttribute('bot-id', botId);
          containerElement.setAttribute('api-endpoint', apiDomain);
          containerElement.setAttribute('darkmode', 'true'); // Technical interface is always dark
          containerElement.style.width = '100%';
          containerElement.style.height = '100%';
          containerElement.style.display = 'block';
          if (containerRef.current) {
            containerRef.current.appendChild(containerElement);
            sdkInstanceRef.current = { destroy: () => containerElement.remove() };
          }
          setIsLoading(false);
          return;
        }

        // Load SDK script - ensure it's the container SDK, not widget
        await new Promise((resolve, reject) => {
          // Verify scriptUrl is for container mode
          if (!scriptUrl.includes('/container')) {
            reject(new Error('SDK URL must be for container mode, not widget mode'));
            return;
          }

          const script = document.createElement('script');
          script.src = scriptUrl;
          script.async = true;
          script.type = 'module'; // Container SDK uses ES modules
          script.setAttribute('data-container', SDK_CONTAINER_ID);
          script.setAttribute('data-bot-id', botId); // Ensure correct bot ID

          const timeout = setTimeout(() => {
            reject(new Error('Timeout loading container SDK'));
          }, 10000);

          script.onload = () => {
            clearTimeout(timeout);
            let attempts = 0;
            const maxAttempts = 20; // Increased attempts for container SDK
            const checkInterval = setInterval(() => {
              if (!containerRef.current) {
                clearInterval(checkInterval);
                setIsLoading(false);
                reject(new Error('Container ref lost during SDK initialization'));
                return;
              }
              // Check for container element first (container mode) - this is what we want
              const container = containerRef.current.querySelector('besper-bot-container');
              if (container) {
                clearInterval(checkInterval);
                // Verify it's the correct bot
                if (container.getAttribute('bot-id') === botId) {
                  // Set dark mode attribute - technical interface is always dark
                  container.setAttribute('darkmode', 'true');
                  sdkInstanceRef.current = { destroy: () => container.remove() };
                  setIsLoading(false);
                  resolve();
                  return;
                }
              }
              // DO NOT fallback to widget - container mode should only use besper-bot-container
              if (attempts++ >= maxAttempts) {
                clearInterval(checkInterval);
                if (!containerRef.current) {
                  setIsLoading(false);
                  reject(new Error('Container ref lost during SDK initialization'));
                  return;
                }
                // Create container element for container mode - ensure it's not a widget
                const containerElement = document.createElement('besper-bot-container');
                containerElement.setAttribute('bot-id', botId);
                containerElement.setAttribute('api-endpoint', apiDomain);
                containerElement.setAttribute('darkmode', 'true'); // Technical interface is always dark
                containerElement.style.width = '100%';
                containerElement.style.height = '100%';
                containerElement.style.display = 'block';
                containerRef.current.appendChild(containerElement);
                sdkInstanceRef.current = { destroy: () => containerElement.remove() };
                setIsLoading(false);
                resolve();
              }
            }, 200); // Check every 200ms
          };

          script.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to load container SDK'));
          };

          // Before appending, ensure no widget SDK scripts are interfering
          const widgetScripts = document.querySelectorAll(`script[src*="/api/v1/sdk/bots/${botId}/widget"]`);
          widgetScripts.forEach(ws => {
            if (ws !== script) {
              console.warn('⚠️ Removing conflicting widget SDK script for technical interface');
              ws.remove();
            }
          });

          document.head.appendChild(script);
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setIsLoading(false);
      }
    };

    initializeSDK();

    return () => {
      if (sdkInstanceRef.current?.destroy) {
        sdkInstanceRef.current.destroy();
        sdkInstanceRef.current = null;
      }
      // Clean up: remove any widget elements that might have been incorrectly created
      if (containerRef.current) {
        const incorrectWidget = containerRef.current.querySelector('besper-bot-widget');
        if (incorrectWidget && incorrectWidget.getAttribute('bot-id') === botId) {
          console.warn('⚠️ Removing incorrectly created widget element from technical interface');
          incorrectWidget.remove();
        }
      }
    };
  }, [apiDomain, botId]); // Added botId to dependencies

  // Apply theme CSS variables to SDK container - apply immediately, don't wait for SDK
  useEffect(() => {
    // Apply dark styles immediately - don't wait for containerRef or SDK
    function applyThemeStyles() {
      const styleId = `besper-sdk-theme-${SDK_CONTAINER_ID}`;
      let styleEl = document.getElementById(styleId);
      
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      
      // Technical interface is always dark - always use dark colors
      const colors = DARK_MODE_COLORS;
      // Target container and SDK elements
      const selector = `#${SDK_CONTAINER_ID}, #${SDK_CONTAINER_ID} besper-bot-container, #${SDK_CONTAINER_ID} besper-bot-widget, besper-bot-container[bot-id="${botId}"], besper-bot-widget[bot-id="${botId}"]`;
      
      styleEl.textContent = `
        /* Apply dark background to container immediately */
        #${SDK_CONTAINER_ID} {
          background-color: ${colors.bgPrimary} !important;
          color: ${colors.textPrimary} !important;
        }
        
        ${selector} {
          /* Typography */
          --besper_fontFamily: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
          
          /* Backgrounds */
          --besper_sidebarBg: ${colors.sidebarBg} !important;
          --besper_sidebarHeaderBg: ${colors.sidebarHeaderBg} !important;
          --besper_chatBg: ${colors.chatBg} !important;
          --besper_chatHeaderBg: ${colors.chatHeaderBg} !important;
          --besper_bgPrimary: ${colors.bgPrimary} !important;
          --besper_bgSecondary: ${colors.bgSecondary} !important;
          
          /* Text colors - comprehensive set */
          --besper_textPrimary: ${colors.textPrimary} !important;
          --besper_textSecondary: ${colors.textSecondary} !important;
          --besper_textTertiary: ${colors.textTertiary} !important;
          --besper_textMuted: ${colors.textMuted} !important;
          --besper_textInverse: ${colors.textInverse} !important;
          --besper_textHeader: ${colors.textHeader} !important;
          --besper_textHeaderAlt: ${colors.textHeaderAlt} !important;
          
          /* Message bubbles */
          --besper_messageUserBg: ${colors.userBubbleBg} !important;
          --besper_messageUserText: ${colors.userBubbleText} !important;
          --besper_messageBotBg: ${colors.botBubbleBg} !important;
          --besper_messageBotText: ${colors.botBubbleText} !important;
          
          /* Borders */
          --besper_border: ${colors.border} !important;
          --besper_borderFocus: ${colors.borderFocus} !important;
          
          /* Primary colors */
          --besper_primary: ${colors.primary} !important;
          --besper_primaryHover: ${colors.primaryHover} !important;
          
          /* Status colors */
          --besper_success: ${colors.success} !important;
          --besper_error: ${colors.error} !important;
          
          /* Other */
          --besper_radiusLg: ${colors.radiusLg} !important;
          --besper_containerBorderRadius: ${colors.containerBorderRadius} !important;
        }
      `;
      
      // Also update darkmode attribute on SDK elements (if they exist)
      const container = document.getElementById(SDK_CONTAINER_ID);
      if (container) {
        const sdkElements = container.querySelectorAll('besper-bot-container, besper-bot-widget');
        sdkElements.forEach(el => {
          el.setAttribute('darkmode', 'true'); // Technical interface is always dark
        });
      }
    }
    
    // Apply styles immediately
    applyThemeStyles();
    
    // Also check periodically for SDK elements and update them
    const container = document.getElementById(SDK_CONTAINER_ID);
    if (container) {
      const checkInterval = setInterval(() => {
        const checkContainer = document.getElementById(SDK_CONTAINER_ID);
        if (!checkContainer) {
          clearInterval(checkInterval);
          return;
        }
        const checkSdkElement = checkContainer.querySelector('besper-bot-container') || checkContainer.querySelector('besper-bot-widget');
        if (checkSdkElement) {
          // Update dark mode attribute when SDK element is found - technical interface is always dark
          checkSdkElement.setAttribute('darkmode', 'true');
          applyThemeStyles(); // Re-apply to ensure styles are set
        }
      }, 200);
      return () => clearInterval(checkInterval);
    }
  }, [SDK_CONTAINER_ID, botId]); // Apply immediately, don't wait for isLoading

  // Theme-aware colors for UI elements
  const themeColors = {
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e0e0e0',
      textPrimary: '#333333',
      textSecondary: '#666666',
    },
    dark: {
      cardBg: '#1a1f2e',
      cardBorder: '#2d3748',
      textPrimary: '#f1f5f9',
      textSecondary: '#94a3b8',
    }
  };

  // Technical interface is always dark - always use dark theme colors
  const colors = themeColors.dark;

  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        color: colors.textPrimary,
        textAlign: 'center',
        background: colors.cardBg,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: '8px'
      }}>
        <div style={{ color: '#e53e3e', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          ⚠️ Failed to Load Chat Interface
        </div>
        <div style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '24px' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'relative',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      border: 'none',
      background: 'transparent',
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
      boxSizing: 'border-box'
    }}>
      {/* Assistant Container */}
      <div 
        ref={containerRef}
        id={SDK_CONTAINER_ID}
        style={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          width: '100%',
          display: 'block',
          position: 'relative',
          overflow: 'hidden',
          margin: 0,
          padding: 0,
          background: DARK_MODE_COLORS.bgPrimary, // Dark background immediately
          border: 'none',
          boxSizing: 'border-box',
        }}
      >
        {isLoading && !error && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: DARK_MODE_COLORS.textPrimary, // White text on dark background
            fontSize: '14px',
            zIndex: 10
          }}>
            Loading assistant...
          </div>
        )}
      </div>
    </div>
  );
};

// Search Tab Component — loads site_search SDK
const SearchTab = () => {
  const [apiDomain, setApiDomain] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [tenantId, setTenantId] = useState('');
  const containerRef = useRef(null);
  const sdkInstanceRef = useRef(null);
  const SDK_CONTAINER_ID = 'bsp-tech-search-container';
  const authCtx = useContext(AuthContext); // may be null if no provider

  // Resolve API domain
  useEffect(() => {
    const loadApiDomain = async () => {
      try {
        const endpoint = await getApiEndpoint('public');
        const url = new URL(endpoint);
        setApiDomain(url.origin);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setIsLoading(false);
      }
    };
    loadApiDomain();
  }, []);

  // Grab auth token & tenant if we have an AuthContext
  useEffect(() => {
    if (authCtx && authCtx.isAuthenticated) {
      authCtx.getAccessToken().then(tok => {
        setAuthToken(tok);
      }).catch(err => {
        console.warn('Failed to acquire access token for search SDK', err);
      });
      setTenantId(authCtx.user?.tenantId || '');
    }
  }, [authCtx]);

  // When authToken or tenantId arrives, update an existing SDK element if present
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const el = container.querySelector('besper-site-search');
      if (el) {
        if (tenantId) el.setAttribute('tenant-id', tenantId);
        if (authToken) {
          el.setAttribute('auth-token', authToken);
          el.setAttribute('sources', 'public,auth');
        }
      }
    }
  }, [authToken, tenantId]);

  // Initialize SDK
  useEffect(() => {
    if (!apiDomain || !containerRef.current) return;

    const initializeSDK = async () => {
      if (sdkInstanceRef.current) return;
      if (!containerRef.current) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const scriptUrl = buildSdkUrl(apiDomain, 'site-search');
        containerRef.current.id = SDK_CONTAINER_ID;

        // Helper to insert element with appropriate attributes
        const attachEl = () => {
          const el = document.createElement('besper-site-search');
          el.setAttribute('api-endpoint', apiDomain);
          el.setAttribute('darkmode', 'true');
          if (tenantId) el.setAttribute('tenant-id', tenantId);
          if (authToken) {
            el.setAttribute('auth-token', authToken);
            el.setAttribute('sources', 'public,auth');
          } else {
            el.setAttribute('sources', 'public');
          }
          el.style.width = '100%';
          el.style.height = '100%';
          el.style.display = 'block';
          if (containerRef.current) {
            containerRef.current.appendChild(el);
            sdkInstanceRef.current = { destroy: () => el.remove() };
          }
        };

        // Check if custom element already registered
        if (customElements.get('besper-site-search')) {
          attachEl();
          setIsLoading(false);
          return;
        }

        // Load SDK script
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = scriptUrl;
          script.async = true;
          const timeout = setTimeout(() => reject(new Error('Timeout loading site-search SDK')), 10000);

          script.onload = () => {
            clearTimeout(timeout);
            // Create web component with same helper logic
            const attachEl = () => {
              const el = document.createElement('besper-site-search');
              el.setAttribute('api-endpoint', apiDomain);
              el.setAttribute('darkmode', 'true');
              if (tenantId) el.setAttribute('tenant-id', tenantId);
              if (authToken) {
                el.setAttribute('auth-token', authToken);
                el.setAttribute('sources', 'public,auth');
              } else {
                el.setAttribute('sources', 'public');
              }
              el.style.width = '100%';
              el.style.height = '100%';
              el.style.display = 'block';
              if (containerRef.current) {
                containerRef.current.appendChild(el);
                sdkInstanceRef.current = { destroy: () => el.remove() };
              }
            };
            attachEl();
            setIsLoading(false);
            resolve();
          };

          script.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to load site-search SDK'));
          };

          document.head.appendChild(script);
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setIsLoading(false);
      }
    };

    initializeSDK();

    return () => {
      if (sdkInstanceRef.current?.destroy) {
        sdkInstanceRef.current.destroy();
        sdkInstanceRef.current = null;
      }
    };
  }, [apiDomain]);

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#f87171', background: '#111827', height: '100%' }}>
        <p>Failed to load Search SDK: {error}</p>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', position: 'relative', background: '#111827' }}>
      {isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
          Loading search…
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

// What's B-esper Tab Component
const WhatBESperTab = () => {
  return (
    <div style={{ 
      padding: '32px', 
      color: '#e2e8f0',
      width: '100%',
      overflowY: 'auto',
      height: '100%'
    }}>
      {/* Header Section */}
      <div style={{ 
        marginBottom: '48px',
        paddingBottom: '32px',
        borderBottom: '1px solid #374151'
      }}>
        <h1 style={{ 
          color: '#e2e8f0', 
          fontSize: '32px',
          fontWeight: 600,
          marginBottom: '16px',
          letterSpacing: '-0.5px'
        }}>
          What's B-esper?
        </h1>
        <p style={{ 
          color: '#9ca3af', 
          fontSize: '18px',
          lineHeight: '1.8'
        }}>
          B-esper is a European-first cloud orchestrator and SaaS marketplace. We provide managed Kubernetes hosting, 
          unified billing, and a distribution channel for software vendors across Europe—enabling companies to host, 
          sell, and scale their software through our platform without building everything from scratch.
        </p>
      </div>

      {/* Platform Overview */}
      <div style={{
        marginBottom: '48px',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        padding: '32px'
      }}>
        <h2 style={{
          color: '#e2e8f0',
          fontSize: '24px',
          fontWeight: 600,
          marginBottom: '24px'
        }}>
          Platform Overview
        </h2>
        <p style={{
          color: '#d1d5db',
          fontSize: '16px',
          lineHeight: '1.8',
          marginBottom: '24px'
        }}>
          B-esper provides a complete infrastructure solution for SaaS companies. Instead of managing servers, 
          configuring Kubernetes clusters, building payment systems, and setting up distribution channels, 
          companies can focus on building their product while we handle the infrastructure, billing, and marketplace.
        </p>
        <p style={{
          color: '#d1d5db',
          fontSize: '16px',
          lineHeight: '1.8',
          margin: 0
        }}>
          Our platform has been built and operated over four years, providing a proven foundation for European 
          software companies to scale their operations with <strong style={{ color: '#60a5fa' }}>European data residency</strong>, 
          <strong style={{ color: '#60a5fa' }}> unified billing</strong>, and <strong style={{ color: '#60a5fa' }}>integrated marketplace distribution</strong>.
        </p>
      </div>

      {/* B-esper Platform Section */}
      <div style={{
        marginBottom: '48px',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        padding: '32px'
      }}>
        <h3 style={{
          color: '#e2e8f0',
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '24px'
        }}>
          The B-esper Platform
        </h3>
        <p style={{
          color: '#d1d5db',
          fontSize: '16px',
          lineHeight: '1.8',
          marginBottom: '32px'
        }}>
          A European-first cloud orchestrator and SaaS marketplace, providing managed Kubernetes hosting, 
          unified billing, and a distribution channel for software vendors across Europe.
        </p>
        
        {/* Platform Capabilities */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '24px',
          marginBottom: '32px'
        }}>
          <div style={{
            background: '#1f2937',
            border: '1px solid #374151',
            borderRadius: '8px',
            padding: '24px'
          }}>
            <div style={{ color: '#60a5fa', fontSize: '14px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              SaaS Hosting
            </div>
            <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
              Managed Kubernetes infrastructure with European data residency. Companies host on their preferred cloud or our managed infrastructure.
            </p>
          </div>
          <div style={{
            background: '#1f2937',
            border: '1px solid #374151',
            borderRadius: '8px',
            padding: '24px'
          }}>
            <div style={{ color: '#60a5fa', fontSize: '14px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Marketplace
            </div>
            <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
              B2B software marketplace with integrated payments. Vendors get discovered by European businesses or host with their own domain.
            </p>
          </div>
          <div style={{
            background: '#1f2937',
            border: '1px solid #374151',
            borderRadius: '8px',
            padding: '24px'
          }}>
            <div style={{ color: '#60a5fa', fontSize: '14px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Partner Network
            </div>
            <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
              Implementation and reseller partners across Europe. Companies leverage our network for local support and maintenance.
            </p>
          </div>
        </div>

        {/* Products */}
        <div style={{
          borderTop: '1px solid #374151',
          paddingTop: '32px'
        }}>
          <div style={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Platform Products
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '24px'
          }}>
            <div style={{
              background: '#111827',
              border: '1px solid #60a5fa',
              borderRadius: '8px',
              padding: '24px'
            }}>
              <div style={{ color: '#e2e8f0', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                B-esper Bot
              </div>
              <div style={{ color: '#60a5fa', fontSize: '12px', fontWeight: 500, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Available Now
              </div>
              <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Enterprise chatbot solution with knowledge base integration, multi-language support, and seamless embedding into customer websites and applications.
              </p>
            </div>
            <div style={{
              background: '#111827',
              border: '1px solid #60a5fa',
              borderRadius: '8px',
              padding: '24px'
            }}>
              <div style={{ color: '#e2e8f0', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                B-esper AI Models
              </div>
              <div style={{ color: '#60a5fa', fontSize: '12px', fontWeight: 500, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                In Development
              </div>
              <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Enterprise chatbot solution with knowledge base integration, multi-language support, and seamless embedding into customer websites and applications.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '48px'
      }}>
        {[
          {
            title: 'European Data Residency',
            description: 'All data processed and stored within European borders. GDPR-compliant infrastructure with full control over data location and sovereignty.',
            color: '#60a5fa'
          },
          {
            title: 'Unified Billing',
            description: 'Single invoice for hosting, marketplace fees, and partner services. Simplified financial management for SaaS companies.',
            color: '#60a5fa'
          },
          {
            title: 'Marketplace Distribution',
            description: 'Reach European businesses through our integrated marketplace. Get discovered by customers or host with your own domain.',
            color: '#10b981'
          },
          {
            title: 'Partner Network',
            description: 'Access implementation and reseller partners across Europe. Leverage local support and maintenance without building your own network.',
            color: '#f59e0b'
          }
        ].map((value, index) => (
          <div
            key={index}
            style={{
              background: '#111827',
              border: '1px solid #374151',
              borderRadius: '8px',
              padding: '24px',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = value.color;
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${value.color}30`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#374151';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <h3 style={{
              color: '#e2e8f0',
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '12px',
              marginTop: 0
            }}>
              {value.title}
            </h3>
            <p style={{
              color: '#9ca3af',
              fontSize: '14px',
              lineHeight: '1.7',
              margin: 0
            }}>
              {value.description}
            </p>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <div style={{
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        padding: '32px',
        textAlign: 'center'
      }}>
        <h3 style={{
          color: '#e2e8f0',
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '16px'
        }}>
          Join the B-esper Community
        </h3>
        <p style={{
          color: '#9ca3af',
          fontSize: '16px',
          lineHeight: '1.8',
          marginBottom: '24px'
        }}>
          Ready to get started? Host your SaaS application on our platform, list it in our marketplace, or connect with our 
          partner network for implementation and support. Contact us to learn more about how B-esper can help scale your business.
        </p>
        <div style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button style={{
            background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(96, 165, 250, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          >
            Learn More
          </button>
          <button style={{
            background: 'transparent',
            border: '1px solid #60a5fa',
            borderRadius: '8px',
            padding: '12px 24px',
            color: '#60a5fa',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

// Bot Upcoming Tab Component
const BotUpcomingTab = () => {
  return (
    <div style={{ 
      padding: '32px', 
      color: '#e2e8f0',
      maxWidth: '900px',
      margin: '0 auto'
    }}>
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '40px',
        paddingBottom: '24px',
        borderBottom: '1px solid #374151'
      }}>
        <h2 style={{ 
          color: '#f9fafb', 
          fontSize: '28px',
          fontWeight: 600,
          marginBottom: '12px'
        }}>
          Roadmap
        </h2>
        <p style={{ 
          color: '#9ca3af', 
          fontSize: '16px',
          lineHeight: '1.6',
          margin: 0
        }}>
          Upcoming capabilities for the B-esper developer platform
        </p>
      </div>

      {/* B-esper AI Section */}
      <div style={{
        background: '#111827',
        border: '1px solid #60a5fa',
        borderRadius: '8px',
        padding: '32px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          color: '#60a5fa', 
          fontSize: '12px', 
          fontWeight: 600, 
          textTransform: 'uppercase', 
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          In Development
        </div>
        <h3 style={{
          color: '#f9fafb',
          fontSize: '22px',
          fontWeight: 600,
          marginBottom: '12px',
          marginTop: 0
        }}>
          B-esper AI Models
        </h3>
        <p style={{
          color: '#d1d5db',
          fontSize: '15px',
          lineHeight: '1.7',
          marginBottom: '24px'
        }}>
          Our proprietary AI models with embedded deontic logic for compliance verification. These models are designed 
          from first principles for European regulatory frameworks, enabling trustworthy AI deployment with mathematical 
          guarantees of constraint adherence. Split compute architecture enables efficient edge-cloud inference with 
          41% energy savings.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px'
        }}>
          <div style={{ background: '#111827', borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: '#60a5fa', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Deontic Logic</div>
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>Formal constraints embedded in training</div>
          </div>
          <div style={{ background: '#111827', borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: '#60a5fa', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Split Compute</div>
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>Edge-cloud inference with IP protection</div>
          </div>
          <div style={{ background: '#111827', borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: '#60a5fa', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Compliance Ready</div>
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>GDPR and AI Act by design</div>
          </div>
        </div>
      </div>

      {/* API Testing Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
        border: '1px solid #60a5fa',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          color: '#60a5fa', 
          fontSize: '12px', 
          fontWeight: 600, 
          textTransform: 'uppercase', 
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          Coming Soon
        </div>
        <h3 style={{
          color: '#f9fafb',
          fontSize: '22px',
          fontWeight: 600,
          marginBottom: '12px',
          marginTop: 0
        }}>
          API Call Testing
        </h3>
        <p style={{
          color: '#d1d5db',
          fontSize: '15px',
          lineHeight: '1.7',
          marginBottom: '24px'
        }}>
          Interactive API testing environment for developers. Test B-esper Bot endpoints, inspect request/response 
          payloads, debug integrations, and validate webhook configurations—all from within this interface.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px'
        }}>
          <div style={{ background: '#111827', borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: '#10b981', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Request Builder</div>
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>Construct and send API calls with custom headers, authentication, and payloads</div>
          </div>
          <div style={{ background: '#111827', borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: '#10b981', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Response Inspector</div>
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>View formatted responses with syntax highlighting and timing metrics</div>
          </div>
          <div style={{ background: '#111827', borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: '#10b981', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Webhook Validation</div>
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>Test webhook endpoints and verify signature validation</div>
          </div>
          <div style={{ background: '#111827', borderRadius: '8px', padding: '16px' }}>
            <div style={{ color: '#10b981', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Code Generation</div>
            <div style={{ color: '#9ca3af', fontSize: '12px' }}>Auto-generate integration code in multiple languages</div>
          </div>
        </div>
      </div>

      {/* Additional Features */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px'
      }}>
        <div style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '8px',
          padding: '20px'
        }}>
          <div style={{ color: '#f59e0b', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
            Planned
          </div>
          <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            SDK Libraries
          </div>
          <p style={{ color: '#9ca3af', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
            Official SDKs for Python, JavaScript, and other languages with type-safe API bindings.
          </p>
        </div>
        <div style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '8px',
          padding: '20px'
        }}>
          <div style={{ color: '#f59e0b', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
            Planned
          </div>
          <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            Analytics Dashboard
          </div>
          <p style={{ color: '#9ca3af', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
            Usage metrics, conversation analytics, and performance monitoring for your B-esper Bot deployments.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Logger;
